import React from 'react';
import { TextInput, View, StyleProp, ViewStyle, TextStyle } from 'react-native';
import MyAppText from './MyAppText';
import { translate } from '../services/translate';
import { useStyles } from 'react-native-unistyles';
import { Controller, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';
import inputStylesheet from '../styles/inputStyles';

type MyEmailInputProps = {
    label: string;
    labelFontSize?: number;
    required?: boolean;
    disabled?: boolean;
    viewStyle?: StyleProp<ViewStyle>;
    labelStyle?: StyleProp<TextStyle>;
    placeholder?: string;
} & ({
    value: string;
    onChangeText: (value: string) => void;
    isInvalid: boolean;
    setIsInvalid: (value: boolean) => void;
    form?: never;
    fieldName?: never;
    customValidations?: never;
    pattern?: never;
    allowEmptyValue?: never;
} | {
    value?: never;
    onChangeText?: never;
    isInvalid?: never;
    setIsInvalid?: never;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form: UseFormReturn<any, any, any>;
    fieldName: string;
    customValidations?: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: (value: any) => string | true;
    };
    pattern?: {
        value: RegExp;
        message: string;
    };
    allowEmptyValue?: boolean;
});

export default function MyEmailInput({
    label,
    value,
    onChangeText,
    required = false,
    disabled = false,
    isInvalid,
    setIsInvalid,
    viewStyle,
    form,
    fieldName,
    customValidations,
    pattern,
    placeholder,
    labelStyle,
    allowEmptyValue
}: MyEmailInputProps) {
    const { styles: defaultInputStyle } = useStyles(inputStylesheet);

    function isEmailValid(email: string) {
        if (!email && !required) {
            return true;
        }
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (allowEmptyValue && (!email || email.length === 0)) {
            return true;
        }

        return emailRegex.test(email);
    }

    if (form && fieldName) {

        const rules: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'> = {};

        if (required) {
            rules.required = translate('required_field');
        }

        if (pattern) {
            rules.pattern = pattern;
        }

        rules.validate = {
            validateEmail: (value) => isEmailValid(value) || translate('invalidEmail')
        };

        if (customValidations) {
            rules.validate = { ...customValidations };
        }

        return <Controller
            control={form.control}
            name={fieldName}
            rules={rules}
            render={({ field, fieldState }) => (
                <View style={[defaultInputStyle.container, viewStyle]}>
                    <View style={defaultInputStyle.labelContainer}>
                        {label && <MyAppText style={labelStyle ? labelStyle : defaultInputStyle.label}>{label}</MyAppText>}
                        {required && label ?
                            <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                            : null}
                    </View>
                    <TextInput
                        onBlur={field.onBlur}
                        value={field.value}
                        onChangeText={field.onChange}
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoCorrect={false}
                        placeholder={placeholder || translate('email')}
                        style={defaultInputStyle.input({
                            invalid: Boolean(fieldState.error),
                            disabled,
                            placeholder: !field.value,
                        })}
                    />
                    {isInvalid
                        ? <MyAppText style={defaultInputStyle.errorMessage}>{translate('invalidEmail')}</MyAppText>
                        : fieldState.error ? <MyAppText style={defaultInputStyle.errorMessage}>{fieldState.error.message}</MyAppText> : null}
                </View>
            )}
        />;

    } else if (value !== undefined && onChangeText !== undefined) {
        return (
            <View style={[defaultInputStyle.container, viewStyle]}>
                <View style={defaultInputStyle.labelContainer}>
                    {label && <MyAppText style={labelStyle ? labelStyle : defaultInputStyle.label}>{label}</MyAppText>}
                    {required && label ?
                        <MyAppText style={defaultInputStyle.textRequired}>(*)</MyAppText>
                        : null}
                </View>
                <TextInput
                    value={value}
                    onChangeText={onChangeText}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    autoCorrect={false}
                    placeholder={placeholder || translate('email')}
                    style={defaultInputStyle.input({
                        invalid: Boolean(isInvalid),
                        disabled,
                        placeholder: !value,
                    })}
                    onBlur={() => {
                        if (!value && !required) {
                            return setIsInvalid(false);
                        }
                        if (!value && required) {
                            return setIsInvalid(true);
                        }

                        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (!regex.test(value)) {
                            return setIsInvalid(true);
                        }
                        return setIsInvalid(false);

                    }}
                />
                {isInvalid ?
                    <MyAppText style={defaultInputStyle.errorMessage}>{translate('invalidEmail')}</MyAppText> : null}
            </View>
        );
    }
}
